// hooks/useCompany.js
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPortfoliosInBatches } from '../../api/portfolioApi';
import { updateCompanyFields } from "../../api/companyApi";

class RoleBasedAccessControls {
    constructor(cid, role) {
        this.cid = cid;
        this.role = role?.toLowerCase?.()
    }

    allowAdminActions(){
        return ['owner', 'admin'].includes(this.role);
    }

    hasCompany(){
        return this.cid !== 'community';
    }
}

const useCompany = () => {
    const dispatch = useDispatch();

    // Get base company data from Redux
    const companyDetails = useSelector(state => state.user.data?.company_details);
    const companyName = companyDetails?.company_name;
    const logoUrl = companyDetails?.logo_url;
    const companyPortfolios = companyDetails?.portfolios;
    const allPortfolios = useSelector(state => state.portfolios.items);
    const userCompanyRole = companyDetails?.role;
    const cid = companyDetails?.cid;
    const accessControls = new RoleBasedAccessControls(cid, userCompanyRole);
    const allowAdminActions = accessControls.allowAdminActions();
    const hasCompany = accessControls.hasCompany();
    const portfolioPathId = companyDetails?.portfolio_path_id;

    // Memoize portfolioIds to prevent unnecessary recalculations
    const portfolioIds = useMemo(() =>
        companyPortfolios?.map(p => p.id) || [],
        [companyPortfolios]
    );

    // Create a map of company portfolio settings
    const companyPortfoliosMap = useMemo(() =>
        companyPortfolios?.reduce((acc, p) => {
            acc[p.id] = p;
            return acc;
        }, {}) || {},
        [companyPortfolios]
    );



    // Memoize the filtered and merged portfolios
    const mergedPortfolios = useMemo(() =>
        allPortfolios
            ?.filter(p => portfolioIds.includes(p.id))
            .map(portfolio => ({
                ...portfolio,
                company: companyPortfoliosMap[portfolio.id] || { max_weight: 1.0 }
            })) || [],
        [allPortfolios, portfolioIds, companyPortfoliosMap]
    );

    // Fetch portfolios if needed
    useEffect(() => {
        if (portfolioIds.length > 0) {
            dispatch(fetchPortfoliosInBatches({
                portfolioIds,
                userId: localStorage.getItem('userId'),
                userObjects: [],
                batchSize: 50
            }));
        }
    }, [dispatch, portfolioIds.join(',')]);

    const updateCompanyPortfolioPath = async (pathId) => {
        console.log(pathId, companyDetails?.cid);
        try {
            await dispatch(updateCompanyFields({
                companyId: companyDetails?.cid,
                updateFields: { portfolio_path_id: pathId }
            }));
        } catch (error) {
            console.error(`Failed to update portfolio_path_id: ${error.message}`);
        }
    };


    return {
        companyDetails,
        companyName,
        logoUrl,
        portfolioIds,
        companyPortfolios,
        companyPortfoliosMap,
        mergedPortfolios,
        allowAdminActions, hasCompany,
        isLoading: !companyDetails,
        updateCompanyPortfolioPath,
        portfolioPathId

    };
};

export default useCompany;